import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import EditableStyleToolbar from './EditableStyleToolbar';
import { computeCssFromStyleValue } from './styleUtils';
import { useFixedPopoverPosition } from './useFixedPopover';

/**
 * Inline WYSIWYG text block. Read mode renders a plain element with the stored
 * per-block style applied. Edit mode turns it into a contentEditable region of
 * the SAME element/class (so typography matches exactly) plus a small floating
 * toolbar for color/background/font-size/weight/align, split by desktop/mobile.
 */
export default function EditableText({
  blockId,
  as = 'div',
  className = '',
  value,
  onChange,
  blockStyles,
  onStylesChange,
  editable = false,
  extraStyle,
}) {
  const [editing, setEditing] = useState(false);
  const [hover, setHover] = useState(false);
  const [viewport, setViewport] = useState('desktop');
  const elRef = useRef(null);
  const wrapRef = useRef(null);
  const toolbarRef = useRef(null);
  const pos = useFixedPopoverPosition(wrapRef, editing, 'above', toolbarRef);

  const desktopStyle = blockStyles?.desktop || {};
  const mobileStyle = blockStyles?.mobile || {};
  const inlineStyle = { ...computeCssFromStyleValue(desktopStyle), ...extraStyle };
  const Tag = as;
  // Inline tags (span/a) need an inline wrapper too, or they break out of a
  // surrounding <p>/<span> (invalid "div inside p" nesting, React hydration warning).
  const Wrap = as === 'span' || as === 'a' ? 'span' : 'div';

  const commit = () => {
    const el = elRef.current;
    if (!el) return;
    const text = el.innerText.replace(/\n+$/, '');
    if (text !== value) onChange(text);
  };

  // The style toolbar is portaled to <body> (see useFixedPopoverPosition's
  // doc comment — a plain absolutely-positioned toolbar here can end up
  // visually covered by a later section's own scroll-reveal stacking
  // context, silently blocking clicks). Once portaled it's no longer a DOM
  // descendant of wrapRef, so a plain onBlur/contains check would close
  // editing the instant focus leaves the contentEditable region and lands on
  // a toolbar control — use a document-level click-outside check instead.
  useEffect(() => {
    if (!editing) return undefined;
    const onPointerDown = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (toolbarRef.current?.contains(e.target)) return;
      commit();
      setEditing(false);
    };
    document.addEventListener('mousedown', onPointerDown, true);
    return () => document.removeEventListener('mousedown', onPointerDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, value]);

  if (!editable) {
    return (
      <Tag className={className} style={inlineStyle} data-block-id={blockId}>
        {value}
      </Tag>
    );
  }

  const startEdit = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setEditing(true);
    requestAnimationFrame(() => {
      const el = elRef.current;
      if (!el) return;
      el.focus();
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    });
  };

  const patchStyle = (viewportKey, patch) => {
    const nextBlock = {
      desktop: { ...(blockStyles?.desktop || {}) },
      mobile: { ...(blockStyles?.mobile || {}) },
    };
    nextBlock[viewportKey] = { ...nextBlock[viewportKey], ...patch };
    onStylesChange(nextBlock);
  };

  const toolbar =
    editing && pos ? (
      <EditableStyleToolbar
        toolbarRef={toolbarRef}
        style={{
          position: 'fixed',
          left: pos.left,
          top: pos.top,
          // .svcpg-toolbar's CSS class has a leftover `bottom` from before this
          // was portal-positioned — without overriding it here too, a fixed
          // element with both top AND bottom set (and no explicit height)
          // gets its height computed from that gap instead of its content,
          // collapsing it to a sliver with everything else spilling out of it.
          bottom: 'auto',
          transform: pos.flipUp ? 'translateY(-100%)' : undefined,
        }}
        viewport={viewport}
        onViewportChange={setViewport}
        styleValue={viewport === 'desktop' ? desktopStyle : mobileStyle}
        onStyleChange={(patch) => patchStyle(viewport, patch)}
      />
    ) : null;

  return (
    <Wrap
      ref={wrapRef}
      className={`svcpg-editable${editing ? ' svcpg-editable--editing' : ''}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {!editing && hover ? (
        <button type="button" className="svcpg-pencil" onClick={startEdit} aria-label="Edit text">
          ✎
        </button>
      ) : null}

      <Tag
        ref={editing ? elRef : null}
        className={`${className}${hover && !editing ? ' svcpg-editable-hover' : ''}`}
        style={inlineStyle}
        data-block-id={blockId}
        contentEditable={editing}
        suppressContentEditableWarning
        onClick={!editing ? startEdit : undefined}
      >
        {value}
      </Tag>

      {toolbar ? createPortal(toolbar, document.body) : null}
    </Wrap>
  );
}
